import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from 'src/redux/store';
import { getCookie } from 'src/common/config/FunctionHelper';
import { loginGTMTrackEvent } from 'src/common/Analytics';
import { loadAnalytics } from 'src/loadAnalytics';
import { posthogService } from 'src/common/services/posthogService';

/**
 * Registers all analytics side-effects on mount:
 * - Defers `loadAnalytics` by 2 s to avoid blocking the initial render
 * - Identifies the user in PostHog (with user-switch & logout handling)
 * - Identifies the user in Microsoft Clarity (with user-switch handling)
 * - Fires a GTM login event when the page finishes loading
 */
export const useAnalyticsSetup = () => {
  const userDetails: any = useSelector((state: RootState) => state.counter.userDetails);
  const activePlanName = useSelector((state: RootState) => state.user.activePlan);

  /** Tracks the last user ID sent to PostHog to avoid redundant identify calls */
  const posthogIdentifiedUserId = useRef<string | null>(null);
  /** Tracks the last user ID sent to Clarity to handle user switches */
  const clarityIdentifiedUserId = useRef<string | null>(null);

  // Load analytics scripts after a short delay
  useEffect(() => {
    const timer = setTimeout(() => loadAnalytics(), 2000);
    return () => clearTimeout(timer);
  }, []);

  // Identify / re-identify the user in PostHog on login, user-switch, or property change
  useEffect(() => {
    if (!userDetails?._id) {
      posthogIdentifiedUserId.current = null;
    } else if (posthogIdentifiedUserId.current !== userDetails._id) {
      if (posthogIdentifiedUserId.current) {
        posthogService.reset();
      }

      posthogService.identify(userDetails._id, {
        email: userDetails.email,
        name: userDetails.name,
        plan: activePlanName,
        role: userDetails.role_type
      });
      posthogIdentifiedUserId.current = userDetails._id;
    }
  }, [userDetails?._id, userDetails?.email, userDetails?.name, userDetails?.role_type, activePlanName]);

  // Identify / re-identify the user in Microsoft Clarity (handles user switches)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.clarity) return;

    if (!userDetails?._id) {
      // User logged out — clear Clarity identity
      if (clarityIdentifiedUserId.current) {
        window.clarity('identify', null);
        clarityIdentifiedUserId.current = null;
      }
    } else if (clarityIdentifiedUserId.current !== userDetails._id) {
      // New user or user switch — re-identify
      window.clarity('identify', String(userDetails.email));
      if (userDetails?.email) {
        window.clarity('set', 'email', userDetails.email);
      }
      clarityIdentifiedUserId.current = userDetails._id;
    }
  }, [userDetails?._id, userDetails?.email]);

  // Fire GTM login event via window.onload
  useEffect(() => {
    const handleLoad = () => {
      const cookieData = getCookie(process.env.REACT_APP_COOKIE_NAME);
      if (cookieData && cookieData !== 'false') loginGTMTrackEvent(true);
    };
    window.onload = handleLoad;
    return () => {
      window.onload = null;
    };
  }, []);
};
